// KNB 容器接入：https://km.sankuai.com/collabpage/2711819563
// import { msi } from "@mtfe/msi-knb-loader";

import { isServer } from "@/lib/constant";
import { Level, reportCustomMetric, reportError } from "@/lib/owl";

// 6.0KB 30ms
const MSI_SCRIPT_URL = "https://s0.meituan.net/bs/knb/msi_0.0.9.js";

// 上报 MSI 初始化成功率
type LoadMSIErrMsg = "success" | "msiNotAvailable" | "msiSignatureFail";

function reportLoadMSISuccessRate(
  isSuccess: boolean,
  errMsg: LoadMSIErrMsg = "success"
) {
  reportCustomMetric({
    key: "loadMSI",
    value: isSuccess ? 1 : 0,
    tags: {
      errMsg,
    },
  });
}

/**
 * 加载 MSI 脚本
 * @returns Promise<void>
 */
function loadMSI() {
  if (isServer) {
    return Promise.reject('MSI not available on server');
  }
  if (window?.FXBridge?.loadMSIPromise) return window?.FXBridge?.loadMSIPromise;
  (window.FXBridge = window.FXBridge || {}).loadMSIPromise = new Promise((resolve, reject) => {
    // 加载 MSI 脚本
    const script = document.createElement("script");
    script.src = MSI_SCRIPT_URL;
    script.onload = () => {
      if (typeof window?.MT_WEB?.msi?.load === "function") {
        Promise.resolve(window.MT_WEB.msi.load())
          .then((isValidateSuccess) => {
            // 检查 MSI 桥鉴权是否成功
            (window.FXBridge = window.FXBridge || {}).loadMSIRes = isValidateSuccess;
            if (isValidateSuccess) {
              resolve();
              reportLoadMSISuccessRate(true, "success");
            } else {
              reject("msiSignatureFail");
              reportLoadMSISuccessRate(false, "msiSignatureFail");
            }
          })
          .catch(() => {
            reject("msiSignatureFail");
            reportLoadMSISuccessRate(false, "msiSignatureFail");
          });
      } else {
        reject("msiNotAvailable");
        reportLoadMSISuccessRate(false, "msiNotAvailable");
      }
    };
    script.onerror = () => {
      reject("msiNotAvailable");
      (window.FXBridge = window.FXBridge || {}).loadMSIPromise = null;
      reportLoadMSISuccessRate(false, "msiNotAvailable");
    };
    document.head.appendChild(script);
  });
  return window.FXBridge.loadMSIPromise;
}

/**
 * 加载 MSI 桥并检查是否可调用
 * @param bridgeAPI MSI 桥方法名
 * @returns Promise<boolean> 是否可调用
 */
export function checkMSIBridgeEnable(bridgeAPI: string): Promise<boolean> {
  // SSR兼容性：服务端渲染时window不存在，返回false
  if (isServer) {
    return Promise.resolve(false);
  }
  const check = (bridgeAPI: string) => {
    if (window?.FXBridge?.loadMSIRes) {
      // MSI 桥鉴权成功
      if (typeof window?.msi?.[bridgeAPI] === "function") {
        return true;
      } else {
        return false;
      }
    } else {
      // MSI 桥鉴权失败
      return false;
    }
  };

  return new Promise((resolve) => {
    if (typeof window?.FXBridge?.loadMSIRes === "boolean") {
      // 已获取到 MSI 桥鉴权结果
      resolve(check(bridgeAPI));
    } else {
      // 未获取到 MSI 桥鉴权结果，等待加载 & 鉴权完成
      loadMSI()
        .then(() => {
          resolve(check(bridgeAPI));
        })
        .catch(() => {
          resolve(false);
        });
    }
  });
}

/**
 * 调用 MSI 桥方法
 * @param bridgeAPI MSI 桥方法名
 * @param props 方法参数
 * @returns
 */
export function callMSIBridge(bridgeAPI: string, props?: Record<string, any>) {
  if (isServer) return;

  checkMSIBridgeEnable(bridgeAPI)
    .then((enable) => {
      if (enable) {
        window?.msi?.[bridgeAPI](props);
      } else {
        // MSI 桥不支持 API 方法
        reportError({
          name: "[callMSIBridge] msiBridgeNotSupport",
          msg: `MSI 不支持 ${bridgeAPI} 方法`,
          category: "bridgeError",
          level: Level.ERROR,
        })
      }
    })
    .catch(() => {
      // MSI 桥调用失败
      reportError({
        name: "[callMSIBridge] msiBridgeCallFail",
        msg: `MSI 调用 ${bridgeAPI} 方法失败`,
        category: "bridgeError",
        level: Level.ERROR,
      })
    });
}

/**
 * 初始化 MSI 桥
 */
export function initMSIBridge() {
  if (isServer) return;

  // window.msi 存在，即已初始化 MSI 桥
  if (!window?.msi) loadMSI();
}
